import React, { useEffect, useState } from 'react';
import { getOneClicks, IOneClick, submitOneClick } from '@api/oneClicksService';

import { ToDoCheckIcon } from "@assets/icons";

import scss from "./OneClicks.module.scss";
import { Button } from 'react-bootstrap';
import VWPDashboardSupplier from '@components/Supplier/Dashboard/SupplierCard';
import Loading from '@ui/Loading';
import Show from '@ui/Show';
import Checkbox from '@ui/Checkbox';

interface OneClicksProps {
    categoryId: number;
    supplierId: number;
    regionId: number;
    supplierName: string;
    userName: string;
    userEmail: string;
    userPhone: string;
    weddingDate: Date;
    onOkButtonClicked?: () => void;
    onCompleted?: () => void;
}

const MessageSent = ({ supplierName, onOkCLick }: { supplierName: string, onOkCLick: () => void }) => (
    <div className={scss.sentEnquirySummary}>
        <ToDoCheckIcon className={scss.checkIcon}/>
        <p className={scss.messageInfo}>Your message has been sent to</p>
        <h3>{supplierName}</h3>
        <p className={scss.infoSubtitle}>Look out for a response via email, phone or SMS soon</p>
        <hr className={scss.sectionSeparator} />
        <Button variant={'primary'} onClick={onOkCLick} data-testid="enquiry-message-sent-ok"> OK, thanks! </Button>
    </div>
);

const OneClicks: React.FC<OneClicksProps> = ({ userName, userEmail, userPhone, weddingDate, categoryId, supplierId, regionId, supplierName, onCompleted, onOkButtonClicked }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [oneClicks, setOneClicks] = useState<IOneClick[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [showMessageSent, setShowMessageSent] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [message, setMessage] = useState<string>("Hi there - I found you on Easy Weddings and would love to find out more about your packages and pricing.");

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const data = await getOneClicks(categoryId, supplierId, regionId, supplierName);
            setSelectedIds(data.map(oneClick => oneClick.id));
            setOneClicks(data);
            setLoading(false);

            if (data.length === 0) {
                onCompleted?.();
            }
        };
        
        fetchData();
    }, []);

    const handleOkButtonClicked = () => {
        setShowMessageSent(false);
        onOkButtonClicked?.();
    }

    const handleCheck = (supplierId: number) => {
        if (selectedIds.indexOf(supplierId) < 0) {
            setSelectedIds([...selectedIds, supplierId]);
        } else {
            setSelectedIds(selectedIds.filter(selectedId => selectedId !== supplierId));
        }
    }

    const handleQuotes = async () => {
        setError('');

        if (selectedIds.length === 0) {
            setError('Please select at least one supplier to send a message to.');
            return;
        }
        else if (message.length === 0) {
            setError('Please enter a message to send.');
            return;
        }

        setSubmitting(true);
        const result = await submitOneClick(userName, userEmail, userPhone, weddingDate, categoryId, oneClicks, selectedIds, message);
        
        if (result.filter(r => r.hasError).length > 0)
        {
            setError('Failed to send enquiries. Please try again later.');
        }
        else {
            onCompleted?.();
        }
        setSubmitting(false);
    }

    return (<>
        {showMessageSent ? 
            <MessageSent supplierName={supplierName} onOkCLick={handleOkButtonClicked} /> : 
            <>
                <div className={scss.oneClicksHeader}>
                    <h2>Want more quotes to compare?</h2>
                    <p>Message <strong>{oneClicks?.length > 0 && oneClicks.length}</strong> similar suppliers instantly!</p>
                </div>
                <div className={scss.oneClicksContainer}>
                    <Show when={!loading} otherwise={<Loading />}>
                        {oneClicks.map((oneClick, idx) =>
                            <VWPDashboardSupplier
                                key={oneClick.id}
                                action={<Checkbox
                                    className={scss.checkbox}
                                    onClick={() => handleCheck(oneClick.id)}
                                    isChecked={selectedIds.indexOf(oneClick.id) >= 0}
                                    testId={`one-click-supplier-${idx}`}
                                />}
                                onCardClick={() => handleCheck(oneClick.id)}
                                cardSize={'small'}
                                supplier={{
                                    name: oneClick.name,
                                    rating: oneClick.averageRating,
                                    reviews: oneClick.reviewCount,
                                    thumbnail: oneClick.thumbnail,
                                    region: oneClick.region,
                                    supplierId: oneClick.id,
                                }}
                            />)}
                    </Show>
                </div>
                <div className={'col-12'}>
                    <fieldset>
                        <div className={'row align-items-center ms-1'}>
                            <div className={'col-12 p-md-0'}>
                                <label className={'text-uppercase small fw-bold mb-2'}>Message to Send</label>
                                {!!error && <span className={scss.errorText}>{error}</span>}
                            </div>
                            <div className={'col-md p-md-0'}>
                                <textarea
                                    rows={2}
                                    className={'form-control'}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    required
                                    disabled={loading}
                                    data-testid="one-click-message"
                                ></textarea>
                            </div>
                            <div className={'col-md-3 mt-3 mt-md-0'}>
                                <Button className={'w-100'} variant={'primary'} disabled={loading || submitting} onClick={handleQuotes} data-testid="one-click-submit">
                                    Get Quotes <i className={'fa-light fa-arrow-right-long ms-1'}></i>
                                </Button>
                            </div>
                        </div>
                    </fieldset>
                </div>
            </>
        }
    </>);
};

export default OneClicks;
